/**
 * I18nService — wrapper centralisé au-dessus de Transloco.
 *
 * Apporte par rapport à Transloco nu :
 *   - Signal Angular natif `currentLocale` / `availableLocales` / `dir`
 *   - Initialisation : localStorage > navigator.language > fr
 *   - Application automatique de `<html dir="rtl" lang="...">`
 *   - Helper `tDyn(text, target?)` pour traduire une valeur dynamique
 *     (description produit, nom de boutique, etc.) via le backend
 *     (POST /i18n/translate-text, cached).
 *
 * Toutes les pages doivent injecter ce service en racine (déjà
 * `providedIn: 'root'`) pour bénéficier de la réactivité.
 */

import { HttpClient, HttpHeaders } from '@angular/common/http';
import {
  Inject,
  Injectable,
  PLATFORM_ID,
  computed,
  effect,
  inject,
  signal,
} from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { TranslocoService } from '@jsverse/transloco';
import { CookieService } from 'ngx-cookie-service';
import { Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';

import { ConfigService } from '../services/config.service';

export type SupportedLocale = 'fr' | 'en' | 'ar-TN';

export interface LocaleOption {
  code: SupportedLocale;
  label: string;
  nativeLabel: string;
  flag: string;
  dir: 'ltr' | 'rtl';
}

export const AVAILABLE_LOCALES: LocaleOption[] = [
  { code: 'fr',    label: 'Français',         nativeLabel: 'Français',        flag: '🇫🇷', dir: 'ltr' },
  { code: 'en',    label: 'English',          nativeLabel: 'English',         flag: '🇬🇧', dir: 'ltr' },
  { code: 'ar-TN', label: 'Arabe tunisien',   nativeLabel: 'العربية تونسي',   flag: '🇹🇳', dir: 'rtl' },
];

const STORAGE_KEY = 'vd-locale';

@Injectable({ providedIn: 'root' })
export class I18nService {
  private transloco = inject(TranslocoService);
  private http = inject(HttpClient);
  private cookies = inject(CookieService);
  private cfg = inject(ConfigService);
  private readonly isBrowser: boolean;

  readonly locales = AVAILABLE_LOCALES;
  readonly currentLocale = signal<SupportedLocale>('fr');
  readonly currentOption = computed<LocaleOption>(() =>
    AVAILABLE_LOCALES.find((l) => l.code === this.currentLocale()) ?? AVAILABLE_LOCALES[0],
  );
  readonly dir = computed<'ltr' | 'rtl'>(() => this.currentOption().dir);
  readonly isRTL = computed<boolean>(() => this.dir() === 'rtl');

  constructor(@Inject(PLATFORM_ID) platformId: object) {
    this.isBrowser = isPlatformBrowser(platformId);
    this.initFromStorage();

    // Applique le DOM (lang/dir) à chaque changement
    effect(() => {
      const opt = this.currentOption();
      this.applyDom(opt);
    });
  }

  private initFromStorage(): void {
    if (!this.isBrowser) return;
    let code: SupportedLocale | null = null;
    try {
      const saved = localStorage.getItem(STORAGE_KEY) as SupportedLocale | null;
      if (saved && AVAILABLE_LOCALES.some((l) => l.code === saved)) {
        code = saved;
      }
    } catch {
      /* noop */
    }

    if (!code) {
      const nav = (navigator.language || 'fr').toLowerCase();
      if (nav.startsWith('ar')) code = 'ar-TN';
      else if (nav.startsWith('en')) code = 'en';
      else code = 'fr';
    }

    this.setLocale(code, /* persist */ false);
  }

  /** Change la locale, persiste, met à jour Transloco + DOM. */
  setLocale(code: SupportedLocale, persist = true): void {
    if (!AVAILABLE_LOCALES.some((l) => l.code === code)) return;
    this.currentLocale.set(code);
    this.transloco.setActiveLang(code);
    if (this.isBrowser && persist) {
      try {
        localStorage.setItem(STORAGE_KEY, code);
      } catch {
        /* noop */
      }
    }
  }

  /** Cycle FR → EN → AR-TN → FR (utilisé par le toggle compact). */
  cycleLocale(): void {
    const idx = AVAILABLE_LOCALES.findIndex((l) => l.code === this.currentLocale());
    const next = AVAILABLE_LOCALES[(idx + 1) % AVAILABLE_LOCALES.length];
    this.setLocale(next.code);
  }

  private applyDom(opt: LocaleOption): void {
    if (!this.isBrowser) return;
    try {
      const html = document.documentElement;
      html.lang = opt.code;
      html.dir = opt.dir;
      document.body?.classList.toggle('vd-rtl', opt.dir === 'rtl');
    } catch {
      /* noop */
    }
  }

  /** Traduit une valeur dynamique (UGC) via le backend (cached). */
  translateDynamic(text: string, target?: SupportedLocale): Observable<string> {
    const targetLocale = target ?? this.currentLocale();
    if (!text || !text.trim()) return of(text);
    if (targetLocale === 'fr') return of(text);
    const url = `${this.cfg.apiUrl}/i18n/translate-text`;
    const token = this.cookies.get('access_token');
    const headers = new HttpHeaders(
      token ? { Authorization: `Bearer ${token}` } : {},
    );
    return this.http
      .post<{ text: string }>(
        url,
        { text, target_locale: targetLocale, source_locale: 'fr' },
        { headers },
      )
      .pipe(
        map((r) => r?.text ?? text),
        catchError(() => of(text)),
      );
  }

  /** Sucre : version synchrone pour Transloco quand on a déjà la clé. */
  t(key: string, params?: any): string {
    return this.transloco.translate(key, params);
  }
}
